import { React, useMemo, memo, useState, useEffect } from 'react'
import { Link } from 'react-router'
import { FaTrash, FaPlus, FaCheckCircle, FaTimesCircle } from 'react-icons/fa'
import { SimpleTable, Box, Button, Icon, Text, Badge, Checkbox, Tooltip, Stack, ConfirmButton, FileViewer, ImageViewer, VideoViewer, ButtonGroup, UserChip, SimplePopover, SimpleStat, Select, TeethChart } from '@/components/lib'
import useStore from '@/store'
import api from '@/api'
import utils, { likertAnswers, likertTotal, tableColumns, ratingPoints, ratingLabelFor } from '@/utils/utils'

import QueryRaiser from './QueryRaiser'
import BMITag from './BMITag'

// Small memoised cell component so value rendering is isolated and only re-runs when its own props change
const ResponseValueCell = memo(function ResponseValueCell ({ dataType, value }) {
  if (value === null || value === undefined) return null

  if (['text', 'paragraph', 'integer', 'float', 'slider', 'calculation'].indexOf(dataType.type) > -1) {
    return <Text>{value.toString()}</Text>
  }

  if (dataType.type === 'rating') {
    // The number alone says very little — "4" means nothing without knowing
    // whether the scale ran to five or to six, or what the fourth point was
    // called. The label is not stored with the response, so it is read from the
    // field as it stands now, which is also what makes re-wording one apply
    // retroactively.
    const label = ratingLabelFor(dataType, value)
    return (
      <Stack direction='row' gap={2} alignItems='baseline'>
        <Text>{value} of {ratingPoints(dataType)}</Text>
        {label && <Badge colorPalette='blue' variant='outline'>{label}</Badge>}
      </Stack>
    )
  }

  if (dataType.type === 'choice') {
    return value.map(chosen => <Badge key={chosen} colorPalette='blue' variant='outline'>{chosen}</Badge>)
  }

  if (dataType.type === 'likert') {
    const answers = likertAnswers(dataType, value)
    if (!answers.length) return null
    const total = likertTotal(value, dataType)
    return (
      <Stack gap={1}>
        {answers.map(answer => (
          <Stack key={answer.key} direction='row' gap={2} alignItems='baseline'>
            <Text fontSize='sm' color='fg.muted'>{answer.label}</Text>
            <Badge colorPalette='blue' variant='outline'>{answer.answerLabel}</Badge>
          </Stack>
        ))}
        {total !== null && <Text fontSize='xs' color='fg.muted'>Total: {total}</Text>}
      </Stack>
    )
  }

  if (dataType.type === 'table') {
    const columns = tableColumns(dataType)
    const rows = Array.isArray(value) ? value : []
    if (!columns.length || !rows.length) return null
    return (
      <Box overflowX='auto'>
        <SimpleTable
          size='sm'
          headers={columns.map(column => column.name)}
          rows={rows.map(row => columns.map(column => {
            const cell = (row ?? {})[column.id]
            if (cell === null || cell === undefined || cell === '') return ''
            return column.type === 'date' ? utils.formatDate(cell) : String(cell)
          }))}
        />
      </Box>
    )
  }

  if (dataType.type === 'date') return <Text>{utils.formatDate(value)}</Text>
  if (dataType.type === 'datetime') return <Text>{utils.formatDateTime(value)}</Text>
  if (dataType.type === 'time') return <Text>{utils.formatTime(value)}</Text>
  if (dataType.type === 'temperature') return <Text>{parseFloat(value)?.toFixed(2)} °C</Text>
  if (dataType.type === 'weight') return <Text>{parseFloat(value)?.toFixed(2)} kg</Text>
  if (dataType.type === 'height') return <Text>{parseFloat(value)?.toFixed(2)} cm</Text>
  if (dataType.type === 'file') return <FileViewer url={value.url} name={value.fileName} displayName={value.name} preview />
  if (dataType.type === 'image') return <ImageViewer url={value.url} withPreview />
  if (dataType.type === 'video') return <VideoViewer url={value.url} thumbUrl={value.thumbUrl} withPreview isProcessing={value.needsVideoProcess} />
  if (dataType.type === 'food') return <Text>{value.amountConsumed}g {value.name}</Text>
  if (dataType.type === 'bmi') return <BMITag bmi={value?.bmi} />

  return null
})

// Internal implementation component; wrapped in a memoised public component at the bottom of the file
function ResponseViewerComponent ({ response, dataTypes, queries, setQueries, responseValidations, setResponseValidations, onDelete, queryRaiserPortalRef, highlightDataTypeId }) {
  const { user, team, trial, getTrialUsers, settings, changeSetting } = useStore()
  // Cache trial users so we don't recompute the list for every ResponseViewer render
  const trialUsers = useMemo(() => getTrialUsers(trial._id), [trial._id, getTrialUsers])

  // Order the data types according to the componentIndex stored on the response so rows are stable and predictable
  const orderedDataTypes = useMemo(() => {
    if (!dataTypes || dataTypes.length === 0 || !response || !response.response) return []
    const orderedResps = Object.keys(response.response).sort((a, b) => {
      return response.response[a]?.componentIndex - response.response[b]?.componentIndex
    })
    return orderedResps.map(dataTypeId => dataTypes.find(dt => dt._id === dataTypeId))
  }, [dataTypes, response])

  // A query raised on the response score names no data type, so it belongs to
  // no row. Without this it would be invisible here - including to the person
  // the assignment email sends to this very page.
  const responseQueries = queries?.filter(q => q.response === response._id && !q.dataType) || []

  // Limit the number of fields shown per response by default to keep the DOM small for large forms
  const [showAllFields, setShowAllFields] = useState(false)
  const MAX_FIELDS_DEFAULT = 5
  const hasMoreFields = orderedDataTypes.length > MAX_FIELDS_DEFAULT
  const visibleDataTypes = showAllFields || !hasMoreFields
    ? orderedDataTypes
    : orderedDataTypes.slice(0, MAX_FIELDS_DEFAULT)

  // When we've been deep-linked to a particular field (e.g. from a query email), make sure
  // it's on screen even if it falls outside the fields shown by default
  const highlightedIndex = useMemo(
    () => (highlightDataTypeId ? orderedDataTypes.findIndex(dt => dt?._id === highlightDataTypeId) : -1),
    [orderedDataTypes, highlightDataTypeId]
  )
  useEffect(() => {
    if (highlightedIndex >= MAX_FIELDS_DEFAULT) setShowAllFields(true)
  }, [highlightedIndex])

  // Source data verification applies to every answered field of the response,
  // not just the ones currently on screen — the table hides fields past the
  // first few, and "verify the whole response" has to mean the whole response.
  const sdvEnabled = utils.hasFeature(team, 'sdv')
  const verifiableDataTypes = useMemo(
    () => orderedDataTypes.filter(dt => dt && response?.response?.[dt._id]?.value !== null && response?.response?.[dt._id]?.value !== undefined),
    [orderedDataTypes, response]
  )
  const verifiedCount = useMemo(
    () => verifiableDataTypes.filter(dt => responseValidations?.some(v => v.response === response._id && v.dataType === dt._id)).length,
    [verifiableDataTypes, responseValidations, response._id]
  )
  const allVerified = verifiableDataTypes.length > 0 && verifiedCount === verifiableDataTypes.length
  const [bulkVerifying, setBulkVerifying] = useState(false)

  // A bulk call is in flight while the user can still tick individual rows, and
  // in the stage and poll views the validation list is shared across every
  // response on screen — so these always update from the latest state rather
  // than the array captured when the header was clicked.
  const bulkFailed = action => err => {
    setBulkVerifying(false)
    window.alert(`The fields could not be ${action}: ${err?.message || 'there was a problem with this request'}`)
  }

  const bulkVerify = checked => {
    if (bulkVerifying) return
    if (checked) {
      const toVerify = verifiableDataTypes.length - verifiedCount
      if (!toVerify) return
      if (!window.confirm(`Are you sure you want to verify ${toVerify === 1 ? 'the remaining field' : `all ${toVerify} unverified fields`} in this response?`)) return
      setBulkVerifying(true)
      api.trials.createResponseValidations(trial._id, response._id, ({ responseValidations: created }) => {
        setBulkVerifying(false)
        setResponseValidations(prev => [...prev, ...(created || [])])
      }, bulkFailed('verified'))
    } else {
      if (!verifiedCount) return
      if (!window.confirm(`Are you sure you want to remove the verification from ${verifiedCount === 1 ? 'the verified field' : `all ${verifiedCount} verified fields`} in this response?`)) return
      setBulkVerifying(true)
      api.trials.deleteResponseValidations(trial._id, response._id, () => {
        setBulkVerifying(false)
        setResponseValidations(prev => prev.filter(v => v.response !== response._id))
      }, bulkFailed('unverified'))
    }
  }

  return (
    <Stack gap={2}>
      {utils.hasFeature(team, 'queries') && responseQueries.length > 0 &&
        <Box display='flex' justifyContent='end'>
          <SimplePopover
            trigger={
              <Button size='xs' variant='outline' colorPalette='orange'>
                {responseQueries.length} {responseQueries.length === 1 ? 'query' : 'queries'} on this response
              </Button>
            }
            header='Queries on the whole response'
            body={
              <Stack spacing={2} maxH='300px' overflowY='auto'>
                {responseQueries.map(query =>
                  <Box key={query._id} p={2} bg={query.status === 'closed' ? 'green.50' : 'red.50'} _dark={{ bg: query.status === 'closed' ? 'green.900' : 'red.900' }} rounded='md'>
                    <Text color='fg.subtle' fontSize='xs'>{utils.formatDateTime(query.createdAt)}</Text>
                    <Text fontWeight='bold'>{query.title}</Text>
                    <Text>{query.description}</Text>
                    {query.responseScore !== undefined && query.responseScore !== null &&
                      <Text fontSize='xs' color='fg.subtle'>Score when raised: {query.responseScore}</Text>}
                  </Box>)}
                <Button as={Link} to={`/trials/${trial._id}/queries`}>View all queries</Button>
              </Stack>
}
          />
        </Box>}

      <SimpleTable
        size='sm'
        rowProps={(row, index) =>
          highlightDataTypeId && visibleDataTypes[index]?._id === highlightDataTypeId
            ? { bg: 'orange.50', _dark: { bg: 'orange.900' } }
            : null}
        headers={['Date', 'Note',
          <Select key='datatype' size='xs' value={settings.responseDataTypeDisplay || 'dataTypeName'} onChange={e => changeSetting('responseDataTypeDisplay', e.target.value)} variant='subtle'>
            <option value='dataTypeName'>Data Type</option>
            <option value='componentTitle'>Question</option>
          </Select>,
          'Value', 'Score',
          sdvEnabled &&
            <Tooltip key='validation' label={allVerified ? 'Remove the verification from every field of this response' : 'Verify every field of this response'}>
              <Stack direction='row' alignItems='center' gap={2}>
                <Checkbox
                  size='sm' colorPalette='green'
                  isChecked={allVerified}
                  disabled={bulkVerifying || !verifiableDataTypes.length}
                  onChange={e => bulkVerify(e.checked)}
                />
                <Text>Validation</Text>
                {verifiedCount > 0 && !allVerified &&
                  <Text fontSize='xs' color='fg.muted' fontWeight='normal'>{verifiedCount}/{verifiableDataTypes.length}</Text>}
              </Stack>
            </Tooltip>,
          <Box key='actions' display='flex' justifyContent='end'>
            {utils.canManageTrial(user, trial) && onDelete &&
              <ConfirmButton
                header='Really reset this response?'
                body='The recorded data for this stage recurrence will be deleted from the system and the participant and/or users will be able to enter the data again from fresh. We recommend noting this action in your trial documentation.'
                trigger={<Button size='2xs' variant='ghost'><Icon><FaTrash /></Icon> Reset</Button>}
                onConfirm={e => onDelete(response._id)}
              />}
          </Box>]}
        rows={visibleDataTypes?.map(dataType => {
          if (!dataType) return null
          const dataResponse = response?.response && response.response[dataType?._id]
          let value = dataResponse?.value
          if (value === null || value === undefined) return null

          if (dataType.type === 'choice' && (typeof value === 'string' || value instanceof String)) {
            value = value.split(',')
          }

          let name = `${dataType.nickname || dataType.name} (${dataType.type})`
          if (settings.responseDataTypeDisplay === 'componentTitle' && dataResponse?.componentTitle) {
            name = dataResponse.componentTitle
          }

          // Per-row lookups for queries and validation; these arrays are expected to be relatively small per response
          const rowQueries = queries?.filter(q => q.response === response._id && q.dataType === dataType._id) || []
          const validation = responseValidations?.find(v => v.response === response._id && v.dataType === dataType._id)

          return [
            utils.formatDateTime(dataResponse.createdAt),
            dataResponse?.updateNote || '',
            <Text key={dataType._id} overflowWrap='break-word' wordBreak='break-word'>{name}</Text>,
            <Box key={dataType._id}>
              <ResponseValueCell dataType={dataType} value={value} />
              {dataType.type === 'promisForm' &&
                <SimplePopover
                  contentProps={{ w: '500px' }}
                  trigger={
                    <Stack direction='row' alignItems='center'>
                      {value.tScore != null && <Badge colorPalette='blue'>T Score: {value.tScore}</Badge>}
                      <Button size='xs' colorPalette='blue' variant='outline'>View</Button>
                    </Stack>
}
                  header={`${value.formName || 'PROMIS form'}`}
                  body={
                    <Stack gap={4} maxH='400px' overflowY='auto'>
                      {/* Limit rows here to avoid rendering extremely large PROMIS tables in one go */}
                      {value?.responses?.length > 0 &&
                        <SimpleTable
                          headers={['Question', 'Answer', 'Answered at']}
                          rows={value?.responses?.slice(0, 200).map((response, j) => [
                            <Text key={`${j}-q`}>{response.questionText}</Text>,
                            response.skipped
                              ? <Badge key={`${j}-a`} colorPalette='gray'>Skipped</Badge>
                              : <Text key={`${j}-a`}>{response.responseLabel}</Text>,
                            <Text key={`${j}-t`}>{utils.formatDateTime(response.createdAt)}</Text>
                          ])}
                        />}
                    </Stack>
}
                />}
              {dataType.type === 'promisBattery' &&
                <SimplePopover
                  contentProps={{ w: '500px' }}
                  trigger={
                    <Stack direction='row' alignItems='center'>
                      {value?.propr != null
                        ? <Badge colorPalette='purple'>PROPr: {value.propr}</Badge>
                        : <Badge colorPalette='blue'>{value?.forms?.length || 0} forms</Badge>}
                      <Button size='xs' colorPalette='blue' variant='outline'>View</Button>
                    </Stack>
}
                  header={dataType.promisBatteryName || 'PROMIS battery'}
                  body={
                    <Stack gap={4} maxH='400px' overflowY='auto'>
                      {value?.propr != null &&
                        <Stack direction='row' alignItems='center' gap={2} p={2} bg='bg.subtle' rounded='md'>
                          <Text fontSize='sm' fontWeight='medium'>Battery PROPr score:</Text>
                          <Badge colorPalette='purple' size='lg'>{value.propr}</Badge>
                        </Stack>}
                      {value?.utilities && Object.keys(value.utilities).length > 0 &&
                        <Box>
                          <Text fontSize='sm' fontWeight='medium' mb={1}>Domain utilities</Text>
                          <SimpleTable
                            headers={['Domain', 'Utility']}
                            rows={Object.entries(value.utilities).map(([k, v]) => [
                              <Text key={`${k}-d`} fontSize='sm'>{k}</Text>,
                              <Text key={`${k}-v`} fontSize='sm'>{v}</Text>
                            ])}
                          />
                        </Box>}
                      {(value?.forms || []).map((form, i) => (
                        <Stack key={form.formOid || i} gap={2}>
                          <Stack direction='row' alignItems='center' justifyContent='space-between'>
                            <Text fontWeight='medium' fontSize='sm'>{form.formName}</Text>
                            {form.tScore != null && <Badge colorPalette='blue'>T Score: {form.tScore}</Badge>}
                          </Stack>
                          {form?.responses?.length > 0 &&
                            <SimpleTable
                              headers={['Question', 'Answer', 'Answered at']}
                              rows={form.responses.slice(0, 200).map((response, j) => [
                                <Text key={`${i}-${j}-q`}>{response.questionText}</Text>,
                                response.skipped
                                  ? <Badge key={`${i}-${j}-a`} colorPalette='gray'>Skipped</Badge>
                                  : <Text key={`${i}-${j}-a`}>{response.responseLabel}</Text>,
                                <Text key={`${i}-${j}-t`}>{utils.formatDateTime(response.createdAt)}</Text>
                              ])}
                            />}
                        </Stack>
                      ))}
                    </Stack>
}
                />}
              {dataType.type === 'stroop' &&
                <SimplePopover
                  contentProps={{ w: '500px' }}
                  trigger={<Button size='xs' colorPalette='blue' variant='outline'>View results</Button>}
                  header='Stroop test results'
                  body={
                    <Stack gap={4} maxH='400px' overflowY='auto'>
                      <Stack direction='row' p={2} bg='bg.subtle'>
                        <SimpleStat name='Attempts' number={value?.length} helpText={`${(value?.length / 60).toFixed(2)} per second`} />
                        <SimpleStat name='Correct guesses' number={value?.filter(v => v.selectedOption.toLowerCase() === v.displayedColourName.toLowerCase()).length} total={value?.length} helpTextAsPercentage />
                      </Stack>
                      {/* Cap rows for Stroop to keep DOM size manageable on large datasets */}
                      <SimpleTable
                        headers={['Displayed', 'Selected', 'Time elapsed (ms)']}
                        rows={value?.slice(0, 500).map((stroopResult, j) => [
                          <span key={`${j}-o`} style={{ color: stroopResult.displayedColour }}>{stroopResult.displayedOption}</span>,
                          <Text key={`${j}-s`}>
                            {stroopResult.selectedOption}
                            {stroopResult.selectedOption.toLowerCase() === stroopResult.displayedColourName.toLowerCase() ? <Icon as={FaCheckCircle} color='green.500' /> : <Box as={FaTimesCircle} color='red.500' display='inline-block' />}
                          </Text>,
                          stroopResult.timeElapsed
                        ])}
                      />
                    </Stack>
}
                />}
              {dataType.type === 'pal' &&
                <SimplePopover
                  contentProps={{ w: '500px' }}
                  trigger={<Button size='xs' colorPalette='blue' variant='outline'>View results</Button>}
                  header='Paired Associates Learning test results'
                  body={
                    <Stack gap={4} maxH='400px' overflowY='auto'>
                      <Stack direction='row' p={2} bg='bg.subtle'>
                        <SimpleStat name='Correct answers' number={value?.filter(r => r.userAnsweredCorrectly)?.length} total={value?.length} helpTextAsPercentage />
                      </Stack>
                      {/* Cap rows for PAL to keep DOM size manageable on large datasets */}
                      <SimpleTable
                        headers={['Prompt', 'Answer', 'Related', 'Correct']}
                        rows={value?.slice(0, 500).map((result, j) => [
                          <Text key={`${j}-q`}>{result.question}</Text>,
                          <Text key={`${j}-a`}>{result.userAnswer}</Text>,
                          <Text key={`${j}-r`}>{result.promptIsRelated ? 'True' : 'False'}</Text>,
                          <Text key={`${j}-c`}>{result.userAnsweredCorrectly ? <Icon as={FaCheckCircle} color='green.500' /> : <Icon as={FaTimesCircle} color='red.500' />}</Text>
                        ])}
                      />
                    </Stack>
            }
                />}
              {dataType.type === 'ravlt' &&
                <SimplePopover
                  contentProps={{ w: '500px' }}
                  trigger={<Button size='xs' colorPalette='blue' variant='outline'>View results</Button>}
                  header='Rey Auditory Verbal Learning Test results'
                  body={
                    <Stack gap={4} maxH='400px' overflowY='auto'>
                      <Stack direction='row' p={2} bg='bg.subtle'>
                        <SimpleStat name='First test recall' number={value?.rememberedTestSets[0]?.length} total={15} helpTextAsPercentage />
                        <SimpleStat name='Final test recall' number={value?.rememberedTestSets[value?.rememberedTestSets?.length - 1]?.length} total={15} helpTextAsPercentage />
                      </Stack>
                      {/* Cap rows for RAVLT to keep DOM size manageable on large datasets */}
                      <SimpleTable
                        headers={['Test set', 'Recalled words', 'Recall']}
                        rows={[
                          ...value?.rememberedTestSets?.slice(0, 50).map((testSet, i) => [
                      `Test ${i + 1}`,
                      testSet?.map(word => <Text key={word} fontSize='xs'>{word}</Text>),
                      `${parseInt(((testSet?.length || 0) / 15) * 100)}%`
                          ]),
                          [
                            'Interruption set',
                            value?.rememberedInterruptionSet?.map(word => <Text key={word} fontSize='xs'>{word}</Text>),
                      `${parseInt(((value?.rememberedInterruptionSet?.length || 0) / 15) * 100)}%`
                          ]
                        ]}
                      />
                    </Stack>
}
                />}
              {dataType.type === 'teethChart' &&
                <SimplePopover
                  contentProps={{ w: '720px' }}
                  trigger={<Button size='xs' colorPalette='blue' variant='outline'>View chart ({Object.keys(value || {}).length} teeth)</Button>}
                  header='Teeth chart'
                  body={<TeethChart value={value} readOnly />}
                />}
            </Box>,
            (dataResponse.score || dataResponse.score === 0) && <Badge key={dataType._id} colorPalette='blue'>{dataResponse.score}</Badge>,
            sdvEnabled &&
              <Box key={dataType._id}>
                <Tooltip
                  label={validation ? `Verified at ${utils.formatDateTime(validation.createdAt)}` : 'Not yet verified'}
                >
                  <Stack direction='row' alignItems='center'>
                    <Checkbox
                      size='sm' colorPalette='green'
                      isChecked={!!validation}
                      disabled={bulkVerifying}
                      onChange={e => {
                        if (e.checked) {
                          if (!window.confirm('Are you sure you want to verify this data?')) return
                          api.trials.createResponseValidation(trial._id, response._id, dataType._id, newValidation => {
                            setResponseValidations(prev => [...prev, newValidation])
                          }, err => window.alert(`This data could not be verified: ${err?.message || 'there was a problem with this request'}`))
                        } else {
                          if (!window.confirm('Are you sure you want to remove this verification?')) return
                          api.trials.deleteResponseValidation(trial._id, response._id, dataType._id, () => {
                            setResponseValidations(prev => prev.filter(s => s._id !== validation._id))
                          }, err => window.alert(`This verification could not be removed: ${err?.message || 'there was a problem with this request'}`))
                        }
                      }}
                    />
                    {validation?.createdByUser &&
                      <UserChip user={validation.createdByUser} compact onlyAvatar />}
                  </Stack>
                </Tooltip>
              </Box>,
            <Box key={dataType._id}>
              {utils.hasFeature(team, 'queries') &&
                <Box display='flex' justifyContent='end'>
                  <ButtonGroup size='xs' isAttached variant='outline' colorPalette={rowQueries.length > 0 ? 'orange' : null}>
                    <SimplePopover
                      trigger={<Button>{rowQueries.length} queries</Button>}
                      header='Queries'
                      body={
                        <Stack spacing={2} maxH='300px' overflowY='auto'>
                          {!rowQueries.length && <Text color='fg.subtle'>No queries have been raised for this data</Text>}
                          {rowQueries.map(query =>
                            <Box key={query._id} p={2} bg={query.status === 'closed' ? 'green.50' : 'red.50'} _dark={{ bg: query.status === 'closed' ? 'green.900' : 'red.900' }} rounded='md'>
                              <Text color='fg.subtle' fontSize='xs'>{utils.formatDateTime(query.createdAt)}</Text>
                              <Text fontWeight='bold'>{query.title}</Text>
                              <Text>{query.description}</Text>
                            </Box>)}
                          <Button as={Link} to={`/trials/${trial._id}/queries`}>View all queries</Button>
                        </Stack>
}
                    />
                    <QueryRaiser
                      portalRef={queryRaiserPortalRef}
                      trialId={trial._id}
                      participantId={response.participant}
                      dataTypeId={dataType._id}
                      responseId={response._id}
                      trigger={<Button><Icon as={FaPlus} /></Button>}
                      allowedAssignees={trialUsers}
                      onRaised={query => setQueries([...queries, query])}
                    />
                  </ButtonGroup>
                </Box>}
            </Box>
          ]
        })}
      />
      {hasMoreFields && (
        <Box display='flex' justifyContent='center'>
          <Button
            size='xs'
            variant='outline'
            onClick={() => setShowAllFields(prev => !prev)}
          >
            {showAllFields ? 'Show fewer fields' : `Show all ${orderedDataTypes.length} fields`}
          </Button>
        </Box>
      )}
    </Stack>
  )
}

export default memo(function ResponseViewer (props) {
  return <ResponseViewerComponent {...props} />
})
